<script>
	// NPM Modules
	import dayjs from 'dayjs';
	// Stores
	import UserBetStatsStore from '@/Stores/User/UserBetStatsStore.js';
	// Components
	import Modal from '@/Components/ModalFlex.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Icon from '@/Components/Icon.svelte';
	import Datepicker from '@/Components/Datepicker.svelte';
	import { CurrencyFormat, NumberFormat } from '@/Services/Helpers';

	export let open = false;
	export let user;

	$: open && defaultRange(1);
	$: stats = (sortCol || sortDesc) && $UserBetStatsStore && sortStats();

	let sending = false;
	let startDate;
	let endDate;
	const statCols = {
		Category: 'bet_category.name',
		Classification: 'classification.name',
		'Client Result': 'client_result',
		Turnover: 'turnover',
		'Bonus Turnover': 'bonus_turnover',
		Percentage: 'percentage',
		'Num. Bets': 'bet_count',
		'Num. Bonus Bets': 'bonus_bet_count',
		'Avg. Bet': 'bet_average'
	};
	let sortCol = 'client_result';
	let sortDesc = true;

	function sortStats() {
		return sortDesc ? $UserBetStatsStore.sortByDesc(sortCol) : $UserBetStatsStore.sortBy(sortCol);
	}

	function onRunReport() {
		sending = true;
		UserBetStatsStore.getUserBetStats(
			user.id,
			dayjs(startDate).format('YYYY-MM-DD'),
			dayjs(endDate).format('YYYY-MM-DD')
		)
			.then()
			.catch()
			.finally(() => (sending = false));
	}

	function defaultRange(weeks) {
		startDate = dayjs().subtract(weeks, 'week').format('YYYY-MM-DD');
		endDate = dayjs().format('YYYY-MM-DD');
		onRunReport();
	}

	function calcTotalPercentage() {
		return ($UserBetStatsStore.sum('client_result') / $UserBetStatsStore.sum('total_turnover')) * 100;
	}

	function calcTotalAverageBet() {
		return $UserBetStatsStore.sum('total_turnover') / 100 / $UserBetStatsStore.sum('total_bet_count');
	}
</script>

{#if open}
	<Modal on:close={() => (open = false)} className="w-full xl:w-1/2 mx-2">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">
			{user.first_name || ''}
			{user.last_name || ''} Betting Stats
		</h2>
		<div class="flex flex-col w-full px-4 pt-2 text-center">
			<div class="flex items-center justify-center mb-4 space-x-4">
				<button class="btn btn-primary focus:outline-none" on:click={() => defaultRange(12)}> 3 months </button>
				<button class="btn btn-primary focus:outline-none" on:click={() => defaultRange(4)}> 1 month</button>
				<button class="btn btn-primary focus:outline-none" on:click={() => defaultRange(1)}> 1 week</button>
				<div>
					<span class="font-semibold">Start Date:</span>
					<Datepicker
						selected={startDate}
						displayFormat="DD/MM/YYYY"
						dateChosen={(date) => {
							startDate = dayjs(date).format('YYYY-MM-DD');
						}}
					/>
				</div>
				<div>
					<span class="font-semibold">End Date:</span>
					<Datepicker
						selected={endDate}
						displayFormat="DD/MM/YYYY"
						dateChosen={(date) => {
							endDate = dayjs(date).format('YYYY-MM-DD');
						}}
					/>
				</div>
				<LoadingButton on:click={onRunReport} loading={sending} className="btn btn-primary">Run Report</LoadingButton>
			</div>
			{#if user}
				<div class="mb-4text-dark-grey">
					<div class="flex items-center px-2 bg-dark-grey text-light">
						<h2 class="flex-1 py-2 text-xl italic text-left font-sans-condensed">Betting Stats</h2>
					</div>
					<div class="mt-2 overflow-auto max-report-height">
						{#if stats && stats.count()}
							<div class="table w-full">
								<div class="table-header-group">
									<div class="table-row text-sm text-center bg-grey text-light">
										{#each Object.entries(statCols) as col, i}
											<div
												class:text-left={i < 1}
												class:pl-2={i < 1}
												class:text-green-500={sortCol === col[1]}
												class="sticky top-0 table-cell py-2 pl-1 cursor-pointer bg-grey"
												on:click={() => {
													if (sortCol === col[1]) sortDesc = !sortDesc;
													sortCol = col[1];
												}}
											>
												<div class="flex justify-center">
													{col[0]}
													{#if col[1] === sortCol}
														<Icon name={sortDesc ? 'chevron-down' : 'chevron-up'} classes="h-2 self-center -ml-1" />
													{/if}
												</div>
											</div>
										{/each}
									</div>
								</div>
								<div class="table-row-group">
									{#each stats.all() as stat}
										<div class="table-row text-sm text-center hover:bg-gray-200">
											<div class="table-cell px-2 py-2 text-left border-b">
												{#if stat.race_type}
													{stat.race_type.name} - {stat.bet_type.name}
												{:else if stat.sport}
													{stat.sport.name}
												{:else}
													{stat.bet_type.name} - {stat.bet_category.name}
												{/if}
											</div>
											<div class="table-cell px-4 py-2 border-b">
												{stat.classification ? stat.classification.name : '-'}
											</div>
											<div class="table-cell px-4 py-2 border-b">
												{CurrencyFormat(stat.client_result)}
											</div>
											<div class="table-cell px-4 py-2 border-b">
												{CurrencyFormat(stat.turnover)}
											</div>
											<div class="table-cell px-4 py-2 border-b">
												{CurrencyFormat(stat.bonus_turnover)}
											</div>
											<div class="table-cell px-4 py-2 border-b">
												{NumberFormat(stat.percentage, true)}%
											</div>
											<div class="table-cell px-4 py-2 border-b">
												{stat.bet_count}
											</div>
											<div class="table-cell px-4 py-2 border-b">
												{stat.bonus_bet_count}
											</div>
											<div class="table-cell px-4 py-2 border-b">
												{CurrencyFormat(stat.bet_average)}
											</div>
										</div>
									{/each}
									<div class="table-row text-sm text-center">
										<div class="sticky bottom-0 table-cell px-2 py-2 bg-white" />
										<div class="sticky bottom-0 table-cell px-2 py-2 bg-white" />
										<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 font-bold bg-white border-t border-grey">
											<div>
												{stats && CurrencyFormat(stats.sum('client_result'))}
											</div>
										</div>
										<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
											<div>
												{stats && CurrencyFormat(stats.sum('turnover'))}
											</div>
										</div>
										<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
											<div>
												{stats && CurrencyFormat(stats.sum('bonus_turnover'))}
											</div>
										</div>
										<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
											<div>
												{stats && NumberFormat(calcTotalPercentage(), true)}%
											</div>
										</div>
										<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
											<div>{stats.sum('bet_count')}</div>
										</div>
										<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
											<div>{stats.sum('bonus_bet_count')}</div>
										</div>
										<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
											<div>
												{stats && CurrencyFormat(calcTotalAverageBet(), true)}
											</div>
										</div>
									</div>
								</div>
							</div>
						{:else}
							<div class="w-full py-10 text-center">No betting stats available for this period</div>
						{/if}
					</div>
				</div>
			{/if}
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}> Cancel</button>
		</div>
	</Modal>
{/if}

<style>
	.max-report-height {
		height: calc(var(--vh, 1vh) * 100 - 20rem);
	}
</style>
